<script setup lang="ts">
import { CircleCheck, Edit, Search } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  Entity,
  EntityContentDto,
  EntityInvestigationDto,
  SysOrgRoleName,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";
import FormResponseTemplate from "../pages/FormResponseTemplate.vue";
import EntityWorkflowsPanel from "./EntityWorkflowsPanel.vue";
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updateEntity", entity: Entity): void;
}>();

const props = defineProps<{
  entity: Entity;
}>();
const entityInstance = ref<Entity>();
const entityContentDto = ref<EntityContentDto>();
const drawerOpen = ref(false);

const editing = ref(false);

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

// Only users with the setup basic role can edit the entity name
const canEditEntityName = computed(() => {
  return hasSetupBasicRole;
});

const activeSteps = ref<string[]>(["form", "siteFootprint"]); // el-collapse items to open

// Investigation data
const investigationData = ref<EntityInvestigationDto | null>(null);
const isInvestigating = ref(false);
const hasInvestigated = ref(false);

const showEntity = async (entity: Entity) => {
  drawerOpen.value = true;
  if (!entity?.guid) {
    showError("Entity instance not found");
    return;
  }
  await apiClient.entity
    .getEntity(entity.guid)
    .then((dto) => {
      entityContentDto.value = dto;
      entityInstance.value = dto.entity;
    })
    .catch(showError);
};

const updateEntity = async () => {
  if (!canEditEntityName.value) {
    showError("You do not have permission to update entities");
    return;
  }
  if (!entityInstance.value?.guid) {
    showError("Entity instance not found");
    return;
  }
  if (!entityInstance.value?.status) {
    showError("Status not found");
    return;
  }
  await apiClient.entity
    .updateEntity(entityInstance.value.sysOrgGuid, entityInstance.value)
    .then((res) => {
      ElMessage.success("Entity updated");
      editing.value = false;

      emit("updateEntity", res);
    })
    .catch(showError);
};

const handleClose = async () => {
  emit("closeDrawer");
};

const investigate = async () => {
  if (!entityInstance.value?.guid) {
    showError("Entity instance not found");
    return;
  }

  isInvestigating.value = true;
  try {
    investigationData.value = await apiClient.entity.getEntityInvestigation(
      entityInstance.value.guid,
    );
    hasInvestigated.value = true;
    ElMessage.success("Investigation completed");
  } catch (error) {
    showError(error);
  } finally {
    isInvestigating.value = false;
  }
};

const getWorkflowDefinitionName = (workflowDefinitionGuid: string): string => {
  const workflowDef = orgInfoStore.MyOrgInfo?.workflowDefinitionDtos?.find(
    (wd) => wd.guid === workflowDefinitionGuid,
  );
  return workflowDef?.name || "Unknown Workflow Definition";
};

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

onMounted(async () => {
  await orgInfoStore.load();
  await showEntity(props.entity);
});
</script>

<template>
  <el-drawer
    v-if="entityInstance?.guid"
    :model-value="drawerOpen"
    :title="entityInstance.name || '\u00A0'"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
    class="entity-drawer"
    @closed="handleClose"
  >
    <template #header>
      <div v-if="editing && canEditEntityName" class="el-drawer__title">
        <h2>
          <el-input
            v-model="entityInstance.name"
            placeholder="Entity Name"
            @keyup.enter="updateEntity"
          />
        </h2>

        <el-button
          class="el-drawer__close-btn"
          :icon="CircleCheck"
          size="large"
          @click="updateEntity"
        ></el-button>
      </div>
      <div v-else class="el-drawer__title">
        <h2>
          {{ entityInstance.name }}
        </h2>
        <el-button
          v-if="canEditEntityName"
          class="el-drawer__close-btn"
          :icon="Edit"
          size="large"
          @click="editing = true"
        ></el-button>
        <el-tag size="large" type="warning">
          <div>
            {{ entityInstance.status ?? "" }}
          </div>
        </el-tag>
      </div>
    </template>

    <div v-if="entityInstance">
      <div class="summary-section">
        <div class="summary-info">
          <div class="summary-info-row">
            <div>
              <span class="sub-header">Entity Type: </span
              >{{ entityInstance.entityDefinition?.name }}
            </div>
          </div>
        </div>
      </div>

      <!-- Linked Workflows/Interactions Panel -->
      <EntityWorkflowsPanel
        v-if="entityInstance.guid"
        :entity-guid="entityInstance.guid"
      />

      <el-collapse v-model="activeSteps">
        <el-collapse-item title="Entity Details Form" name="form">
          <FormResponseTemplate
            style="margin-top: 1em"
            :form-guid="entityInstance.formResponse.formDefinitionGuid"
            :response-guid="entityContentDto?.formResponse.guid"
            :entity="entity"
          >
          </FormResponseTemplate
        ></el-collapse-item>
        <el-collapse-item title="Site Footprint" name="siteFootprint">
          <div class="site-footprint-content">
            <div class="investigate-section">
              <el-button
                type="primary"
                :icon="Search"
                :loading="isInvestigating"
                @click="investigate"
              >
                {{ hasInvestigated ? "Refresh Investigation" : "Investigate" }}
              </el-button>
            </div>

            <div v-if="investigationData" class="investigation-results">
              <div class="result-section">
                <h4>Login Accounts</h4>
                <el-table
                  :data="investigationData.loginAccounts ?? undefined"
                  stripe
                  style="width: 100%"
                >
                  <el-table-column prop="loginName" label="Login Name" />
                  <el-table-column prop="email" label="Email" />
                </el-table>
              </div>

              <!-- Workflows Section -->
              <div class="result-section">
                <h4>Workflows</h4>
                <el-table
                  :data="investigationData.workflows"
                  stripe
                  style="width: 100%"
                >
                  <el-table-column prop="workflowName" label="Workflow Name" />
                  <el-table-column label="Workflow Definition">
                    <template #default="scope">
                      {{
                        getWorkflowDefinitionName(
                          scope.row.workflowDefinitionGuid,
                        )
                      }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <!-- SysOrg Groups Section -->
              <div class="result-section">
                <h4>Organization Groups</h4>
                <el-table
                  :data="investigationData.orgGroups"
                  stripe
                  style="width: 100%"
                >
                  <el-table-column prop="groupName" label="Group Name" />
                </el-table>
              </div>

              <!-- Form Responses Section -->
              <div class="result-section">
                <h4>Form Responses</h4>
                <el-table
                  :data="investigationData.formResponses"
                  stripe
                  style="width: 100%"
                >
                  <el-table-column
                    prop="formResponseName"
                    label="Form Response Name"
                  />
                  <el-table-column prop="workflowName" label="Workflow Name" />
                  <el-table-column label="Workflow Definition">
                    <template #default="scope">
                      {{
                        getWorkflowDefinitionName(
                          scope.row.workflowDefinitionGuid,
                        )
                      }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Updated At">
                    <template #default="scope">
                      {{ formatDate(scope.row.updatedAt) }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <!-- No Results Message -->
              <div
                v-if="
                  investigationData.workflows.length === 0 &&
                  investigationData.orgGroups.length === 0 &&
                  investigationData.formResponses.length === 0
                "
                class="no-results"
              >
                <el-empty
                  description="No investigation data found for this entity"
                />
              </div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
    <div v-else>
      <el-empty description="No entity form found" />
    </div>
  </el-drawer>
</template>

<style lang="less">
.entity-drawer {
  .el-drawer__body {
    overflow-y: auto;
  }
  .el-drawer__header {
    .el-drawer__title {
      display: flex;
      align-items: center;
      .el-input__inner {
        font-size: 1.2rem;
      }
    }
  }

  .form {
    margin-bottom: 1em;
  }
  .archive-btn {
    position: absolute;
    bottom: 1em;
    right: 1em;
    color: #afafaf;
  }

  .entity-info {
    display: flex;
    gap: 1em;

    table {
      width: 100%;
      td {
        padding: 0.5em;
      }
    }
  }
}

.summary-section {
  display: grid;
  grid-template-columns: auto auto;

  .summary-info {
    display: flex;
    flex-direction: row;
    gap: 0.5em;
    flex-wrap: wrap;
    margin-bottom: 1em;
    .summary-info-row {
      display: flex;
      align-items: center;
      gap: 2em;
      flex-wrap: wrap;
      text-wrap: nowrap;
    }
  }
}
.el-collapse-item__header {
  font-size: 1.15em;
  border-radius: 10px;
  padding: 0.3rem 1rem;
  background-color: rgba(229, 229, 229, 0.598);
  box-shadow: inset 0 -1.5px 0 rgba(0, 0, 0, 0.1);
  line-height: normal;
  height: fit-content;
  color: inherit;
  font-weight: inherit;
}

.el-collapse-item {
  &.is-active {
    .el-collapse-item__header {
      background-color: var(--el-color-primary-light-7);
    }
  }
}

.site-footprint-content {
  .investigate-section {
    margin: 0.5em 0 1em 0.5em;
  }

  .investigation-results {
    .result-section {
      margin-bottom: 2em;
      padding: 0 1em;
      border: 1px solid var(--el-border-color);
      box-shadow: outset 1px 1px 5px rgba(0, 0, 0, 0.1);

      h4 {
        font-size: 1.1em;
        font-weight: 600;
        margin-bottom: 0.8em;
        color: var(--el-color-primary);
        border-bottom: 1px solid var(--el-border-color-light);
        padding-bottom: 0.5em;
      }
    }

    .no-results {
      margin-top: 2em;
      text-align: center;
    }
  }
}
</style>
